/**
 * Theme Utilities
 *
 * Provides functions for applying dynamic theme colors based on company branding.
 * Generates color shades (50-950) from a base hex color and injects them as CSS custom properties.
 */

import type { Company } from '@shared/types';

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert hex color to RGB string (for CSS variables)
 */
function hexToRgbString(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '59 130 246'; // Fallback to blue
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

/**
 * Convert RGB to HSL
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to hex
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generate color shades (50-950) from a base hex color
 * Uses HSL manipulation to create lighter and darker variants
 * Intelligently handles both light and dark base colors
 */
export function generateColorShades(hexColor: string): Record<string, string> {
  const rgb = hexToRgb(hexColor);
  if (!rgb) {
    // Fallback to blue if invalid color
    return generateColorShades('#3B82F6');
  }

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Adjust saturation for very dark or very light colors
  const adjustedSaturation = hsl.l < 20 || hsl.l > 80 ? Math.min(hsl.s, 30) : hsl.s;

  // Generate shades using lightness adjustments
  // For dark base colors (l < 50), we use the base color for darker shades
  // For light base colors (l >= 50), we use the base color for lighter shades
  const isDark = hsl.l < 50;

  const shades: Record<string, string> = {
    50: hslToHex(hsl.h, adjustedSaturation, 95),
    100: hslToHex(hsl.h, adjustedSaturation, 90),
    200: hslToHex(hsl.h, adjustedSaturation, 80),
    300: hslToHex(hsl.h, adjustedSaturation, 70),
    400: hslToHex(hsl.h, adjustedSaturation, 60),
    500: hexColor, // Base color
    600: isDark
      ? hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 5, 10))
      : hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 10, 40)),
    700: isDark
      ? hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 8, 8))
      : hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 20, 30)),
    800: isDark
      ? hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 10, 6))
      : hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 30, 20)),
    900: isDark
      ? hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 12, 4))
      : hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 40, 10)),
    950: isDark
      ? hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 14, 2))
      : hslToHex(hsl.h, adjustedSaturation, Math.max(hsl.l - 45, 5)),
  };

  return shades;
}

/**
 * Get the effective primary color for theming
 * Dark mode priority: settings.branding.darkMode.primaryColor > primaryColor > brandColor > default
 * Light mode priority: primaryColor > brandColor > default
 */
export function getEffectivePrimaryColor(
  company: Company | null,
  theme?: 'light' | 'dark'
): string {
  if (!company) return '#3B82F6';

  // If dark mode and dark mode color is set, use it
  if (theme === 'dark' && company.settings?.branding?.darkMode?.primaryColor) {
    return company.settings.branding.darkMode.primaryColor;
  }

  // Otherwise use the standard priority
  return company.primaryColor || company.brandColor || '#3B82F6';
}

/**
 * Apply theme colors to the document as CSS custom properties
 * This allows Tailwind to use dynamic colors via the primary-* classes
 */
export function applyTheme(company: Company | null, theme?: 'light' | 'dark'): void {
  const primaryColor = getEffectivePrimaryColor(company, theme);
  const shades = generateColorShades(primaryColor);
  const root = document.documentElement;

  // Apply CSS custom properties as RGB values (for Tailwind compatibility)
  Object.entries(shades).forEach(([shade, color]) => {
    const rgbValue = hexToRgbString(color);
    root.style.setProperty(`--color-primary-${shade}`, rgbValue);
  });
}

/**
 * Remove theme colors from the document
 * Resets to default Tailwind colors
 */
export function removeTheme(): void {
  const root = document.documentElement;
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

  shades.forEach((shade) => {
    root.style.removeProperty(`--color-primary-${shade}`);
  });
}
